import { Grid } from '@mui/material';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Header, Page } from 'redesign';
import { shiftAlerts, useAlert } from 'shared/components/alerts';
import { paths } from 'ogp/paths';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useUpdatePlanningPath } from 'ogp/components/views/planning/planning/hooks/use-get-path-helpers';
import { useGetWorkTemplate } from 'api';
import { useSiteDetection } from 'shared/hooks';
import { mapWorkTemplateToDetails } from 'api/services/work-template/work-template.utils';
import { ToolboxFormCreate } from '../../../common/toolbox-form-create';
import { transformData } from '../../utils/transform-data';
import { useOgpCreateShift } from '../../hooks/ogp/use-ogp-create-shift';
import { OgpShiftForm } from '../ogp-shift-form';
import { OGPShiftFormData } from '../../types/shift-forms.types';

export const OgpShiftCreate = () => {
  const { alertSuccess, alertError } = useAlert();
  const formMethods = useForm<OGPShiftFormData>({
    defaultValues: {
      location: null,
    },
  });

  const { site } = useSiteDetection();
  const jobId = formMethods.watch('jobId');
  const jobDetailsQuery = useGetWorkTemplate(site, jobId, !!jobId);

  const updatePlanningPath = useUpdatePlanningPath();
  const generatePath = useCompanyRelativePath();
  const navigate = useNavigate();

  const mutation = useOgpCreateShift({
    onSuccess: (shiftId) => {
      const shiftDetailPath = updatePlanningPath({
        shiftDetailId: shiftId,
      });
      alertSuccess(shiftAlerts.success.created);
      navigate(shiftDetailPath);
    },
    onError: (e) => alertError(e),
  });

  const onCancel = useCallback(() => {
    navigate(generatePath(paths.ShiftPlanning));
  }, [navigate, generatePath]);

  const handleSubmit = useCallback(() => {
    formMethods.handleSubmit((shiftFormData: OGPShiftFormData) => {
      const data = transformData(shiftFormData);
      mutation.mutate({ ...data, jobId: shiftFormData.jobId });
    })();
  }, [formMethods, mutation]);

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText="Nieuwe shift aanmaken" />}
        toolbox={
          <ToolboxFormCreate
            onSubmit={handleSubmit}
            mutationStatus={mutation.status}
            onCancel={onCancel}
          />
        }
      >
        <Grid container>
          <Grid item xs={12}>
            <OgpShiftForm
              mode="create"
              workDetails={mapWorkTemplateToDetails(jobDetailsQuery.data)}
            />
          </Grid>
        </Grid>
      </Page>
    </FormProvider>
  );
};
